import joi from 'joi';
import { Map, Record as record } from 'immutable';
import _ from 'lodash';

import trainTime from "../lib/TrainTime";
import config from "../config";
import ValidationMixin from './ValidationMixin';
import validation from '../validation';
import TicketMetadata from "./TicketMetadata";
import { construct, fields, resolve } from "../helpers/LinkMapper";
import { translate as t } from "../lib/I18n";
import Address from "./customer/Address";

const min5Yold = trainTime.duration('P5Y');
const min16Yold = trainTime.duration('P16Y');
const customerTitles = config.get('customerTitles') || [];

const defaultUserEmptyValues = [
    'id',
    'title',
    'firstName',
    'lastName',
    'isOver16',
    'yearOfBirth',
    'monthOfBirth',
    'dayOfBirth',
    'addressLine1',
    'addressLine2',
    'addressLine3',
    'townCity',
    'postcode',
    'number',
    'nickname',
    'linkCard',
];

const defaults = {
    id: null,
    ticketId: null,
    title: customerTitles[0],
    firstName: null,
    lastName: null,
    isOver16: true,
    yearOfBirth: null,
    monthOfBirth: null,
    dayOfBirth: null,
    addressLine1: null,
    addressLine2: null,
    addressLine3: null,
    townCity: null,
    country: config.get('defaultDeliveryCountry'),
    postcode: null,
    number: null,
    nickname: null,
    linkCard: false,
    metaData: null,
};

class Photocard extends record(defaults) {

    static fromJSON(json = {}) {
        return construct(Photocard, json, {
            id: fields.number,
            ticketId: fields.number,
            title: json.title ? fields.string : customerTitles[0],
            firstName: fields.string,
            lastName: fields.string,
            isOver16: !!json.isOver16,
            yearOfBirth: json.yearOfBirth ? fields.number : null,
            monthOfBirth: json.monthOfBirth ? fields.number : null,
            dayOfBirth: json.dayOfBirth ? fields.number : null,
            addressLine1: fields.string,
            addressLine2: fields.string,
            addressLine3: fields.string,
            townCity: fields.string,
            country: config.get('defaultDeliveryCountry'),
            postcode: fields.string,
            number: fields.string,
            nickname: fields.string,
            linkCard: fields.bool,
            metaData: resolve(json.metaData).as(TicketMetadata) || new Map(),
        });
    }

    getComputedValues() {
        const dateOfBirth = this.getDateOfBirth();
        const isInFuture = !!(dateOfBirth && dateOfBirth.isValid() && dateOfBirth.isAfter(trainTime()));
        const isInvalidDate = !!(dateOfBirth && !dateOfBirth.isValid());
        const isTooYoung = !!(dateOfBirth && !isInvalidDate && this.isTooYoung(dateOfBirth, min5Yold, 'isAfter'));
        const isOver16WithoutFlag = !!(dateOfBirth && !isInvalidDate && this.isTooYoung(dateOfBirth, min16Yold, 'isBefore'));
        return {
            isInFuture,
            isInvalidDate,
            isTooYoung,
            isOver16WithoutFlag
        };
    }

    getSchema () {
        const defaultErrorMessage = { 'number.base': t('alertmessage.choose') };

        return {
            title: validation.title(t('alertmessage.required.short')).required(),
            firstName: validation.photocardHolder(),
            lastName: validation.photocardHolder(t('alertmessage.badPhotocardLastName')),

            yearOfBirth: joi.when('isOver16', {
                is: false,
                then: joi.number().integer().messages(defaultErrorMessage),
                otherwise: joi.any()
            }),

            monthOfBirth: joi.when('isOver16', {
                is: false,
                then: joi.number().integer().min(1).max(12).messages(defaultErrorMessage),
                otherwise: joi.any()
            }),

            dayOfBirth: joi.when('isOver16', {
                is: false,
                then: joi.number().integer().min(1).max(31).messages(defaultErrorMessage),
                otherwise: joi.any()
            }),

            isInFuture: joi.any().valid(null, false).messages({
                'any.only': t('alertmessage.date.future'),
            }),

            isInvalidDate: joi.any().valid(false).messages({
                'any.only': t('alertmessage.date.invalid'),
            }),

            isTooYoung: joi.any().valid(false).messages({
                'any.only': t('alertmessage.date.tooYoungPhotocard'),
            }),

            isOver16WithoutFlag: joi.any().valid(false).messages({
                'any.only': t('alertmessage.date.under16Photocard'),
            }),

            addressLine1: validation.addressLine().required(),
            addressLine2: validation.textMaxLength(),
            addressLine3: validation.textMaxLength(),
            townCity: validation.townCity(),
            postcode: validation.postcode(),
            number: joi.any().when('nickname', {
                is: 'sTicket',
                then: joi.any(),
                otherwise: validation.requiredString(3, 11, 'alertmessage.photocardNumber')
            }),
            nickname: joi.any().when('linkCard', {
                 is: true,
                 then: validation.requiredString(),
                 otherwise: joi.any()
            })
        };
    }

    _setDefaultFields(field, value) {
        if (value || value === false) {
            return value;
        } else {
            switch (field) {
                case 'title':
                    return customerTitles[0];
                case 'isOver16':
                    return true;
                case 'linkCard':
                    return false;
                default:
                    return null;
            }
        }
    }

    static getDeliveryPhotocard () {
        const deliverySmartCard = new Photocard();
        return deliverySmartCard.set('metaData', new TicketMetadata());
    }

    isStored () {
        return !!this.id;
    }

    isEmpty () {
        const filterObjectProperty = (schema) => {
            return Object.keys(schema)
                .filter(key => defaultUserEmptyValues.includes(key))
                .reduce((obj, key) => {
                    obj[key] = schema[key];
                    return obj;
                }, {});
        };

        return _.isEqual(filterObjectProperty(this.toJS()), filterObjectProperty(defaults));
    }

    equalToFirstPhotocard(order) {
        return defaultUserEmptyValues.every(key => {
            return this[key] === order.delivery.photocards.get(0)[key];
        });
    }

    setTicketData(ticketId, metaData, storedData) {
        const getStoredData = () => {
            if (storedData) {
                return {
                    ...defaultUserEmptyValues.reduce((current, propertyName) => {
                        return { ...current, ...{ [propertyName]: this._setDefaultFields(propertyName, storedData.get(propertyName)) } };
                    }, {})
                };
            }
            return {};
        };
        return new Photocard({
            ticketId: ticketId,
            metaData: new TicketMetadata(metaData),
            linkCard: false,
            ...getStoredData(),
        });
    }

    setAddressInformation(address) {
        return this.merge({
            addressLine1: address.get('line1') || null,
            addressLine2: address.get('line2') || null,
            addressLine3: address.get('line3') || null,
            townCity: address.get('town') || null,
            postcode: address.get('postCode') || null,
        });
    }

    getAddressInformation() {
        return new Address({
            line1: this.get('addressLine1'),
            line2: this.get('addressLine2'),
            line3: this.get('addressLine3'),
            town: this.get('townCity'),
            postCode: this.get('postcode'),
        });
    }

    mergeTicketData(newObj) {
        return this.merge({
            linkCard: false,
            ...defaultUserEmptyValues.reduce((current, propertyName) => {
                return { ...current, ...{ [propertyName]: this._setDefaultFields(propertyName, newObj.get(propertyName)) } };
            }, {})
        });
    }

    unsetInformation() {
        return this.merge({
            ...defaultUserEmptyValues.reduce((current, propertyName) => {
                return { ...current, ...{ [propertyName]: this._setDefaultFields(propertyName, null) } };
            }, {})
        });
    }

    unsetSaveStatus() {
        return this.set('linkCard', false);
    }

    hasAddressInformation() {
        return !!(this.addressLine1 || this.addressLine1 || this.addressLine3 || this.townCity || this.postcode);
    }

    toString () {
        return `${this.nickname} - ${this.firstName} ${this.lastName} - ${this.number}`;
    }

    getDateOfBirth () {
        const { dayOfBirth, monthOfBirth, yearOfBirth, isOver16 } = this;
        return !isOver16 && dayOfBirth && monthOfBirth && yearOfBirth && trainTime(`${yearOfBirth}-${monthOfBirth}-${dayOfBirth}`, 'YYYY-MM-DD');
    }

    isTooYoung (dateOfBirth, minAge, timePeriod) {
        const youngest = trainTime().subtract(minAge);
        return dateOfBirth && dateOfBirth.isValid() && dateOfBirth[timePeriod](youngest);
    }
}

Object.assign(Photocard.prototype, ValidationMixin);

export default Photocard;
